import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, Loader2, Send } from 'lucide-react';
import { useRecruitingShops, useCreateConnectRequest, useMyConnectRequests } from '@/hooks/useConnectApi';

const ROLE_LABELS: Record<string, string> = {
  technician: 'Technician',
  advisor: 'Advisor',
  manager: 'Manager',
  student: 'Student',
  instructor: 'Instructor',
  school_admin: 'School Admin',
};

export function ShopDirectory() {
  const { data: shops, isLoading } = useRecruitingShops();
  const { data: myRequests } = useMyConnectRequests();
  const sendRequest = useCreateConnectRequest();

  const hasPendingTo = (shopId: string) =>
    myRequests?.some(r => r.to_shop_id === shopId && r.status === 'pending');

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Recruiting Shops</CardTitle>
        </div>
        <CardDescription>Shops actively looking for technicians, advisors, and managers in your area.</CardDescription>
      </CardHeader>
      <CardContent>
        {!shops?.length ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No recruiting shops found nearby.</p>
        ) : (
          <div className="space-y-3">
            {shops.map((shop) => (
              <div key={shop.id} className="flex items-center justify-between rounded-lg border p-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10">
                    <Building2 className="h-4 w-4 text-primary" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium">{shop.name}</p>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {shop.hiring_roles?.map((role: string) => (
                        <Badge key={role} variant="outline" className="text-xs">
                          {ROLE_LABELS[role] || role}
                        </Badge>
                      ))}
                    </div>
                    {shop.hiring_blurb && (
                      <p className="text-xs text-muted-foreground line-clamp-2">{shop.hiring_blurb}</p>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant={hasPendingTo(shop.id) ? 'outline' : 'default'}
                  disabled={hasPendingTo(shop.id) || sendRequest.isPending}
                  onClick={() =>
                    sendRequest.mutate({
                      from_type: 'user',
                      to_type: 'shop',
                      to_shop_id: shop.id,
                    })
                  }
                >
                  {hasPendingTo(shop.id) ? 'Pending' : (
                    <>
                      <Send className="h-3.5 w-3.5 mr-1.5" />
                      Connect
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
